import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Building2, Target, Leaf, Cpu, HeartPulse,
  Rocket, Users, Globe, Newspaper, Phone, Mail, MapPin, ArrowRight,
} from 'lucide-react';
import './AboutPage.css';

const values = [
  { Icon: Leaf,       title: 'Sustainability First', desc: 'A fully electric, zero-emission fleet built to decarbonise logistics across Thailand and beyond.' },
  { Icon: Cpu,        title: 'Technology-Driven',    desc: 'IoT sensors, AI routing, and a cloud control tower deliver total visibility and reliability.' },
  { Icon: HeartPulse, title: 'Mission-Critical',     desc: 'We start with healthcare — where compliance, traceability, and trust are non-negotiable.' },
];

const careerPerks = [
  { Icon: Rocket, title: 'Build the Future',     desc: 'Shape a next-generation EV logistics platform from the ground up.' },
  { Icon: Users,  title: 'Mission-Led Team',     desc: 'Work alongside people who care about sustainability and impact.' },
  { Icon: Globe,  title: 'Regional Ambition',    desc: 'Grow with us as we scale across Thailand and into wider Asia.' },
];

const news = [
  {
    tag: 'Phase 1 · Now',
    date: '2026',
    title: 'Healthcare Logistics Launch',
    desc: 'Axion deploys 4-wheel EV trucks engineered for temperature-controlled medical and pharmaceutical delivery, fully GDP / GDPMD compliant.',
  },
  {
    tag: 'Phase 2 · Upcoming',
    date: 'Next',
    title: 'Urban Last-Mile Expansion',
    desc: 'Nimble EV bikes and compact vehicles join the fleet to power fast, zero-emission deliveries across dense urban zones.',
  },
  {
    tag: 'Phase 3 · Future',
    date: 'Future',
    title: 'Regional Transport Network',
    desc: 'Heavy-duty EV trucks scale Axion into long-haul regional logistics, connecting cities with a fully connected EV network.',
  },
];

const contact = [
  { Icon: Phone, label: 'Call Us',  value: '082-992-4569', href: 'tel:+66829924569' },
  { Icon: Mail,  label: 'Email',    value: 'dev@akkra.co.th', href: 'mailto:dev@akkra.co.th' },
  { Icon: MapPin, label: 'Visit Us', value: '118/1 Rama VI Road, Phaya Thai Subdistrict, Phaya Thai District, Bangkok 10400, Thailand', href: 'https://maps.google.com/?q=118/1+Rama+VI+Road+Phaya+Thai+Bangkok+10400+Thailand' },
];

export default function AboutPage() {
  const location = useLocation();

  // Scroll to the section named in the URL hash (#about, #careers, #news, #contact)
  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <div className="about-page">
      {/* ── Hero ── */}
      <section className="about-hero">
        <div
          className="about-hero-bg"
          style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/vehicle-1.png)` }}
        />
        <div className="about-hero-overlay" />
        <div className="about-hero-grid" />
        <div className="about-hero-content">
          <span className="about-eyebrow">Who We Are</span>
          <h1>About <span className="about-accent">Axion Mobility</span></h1>
          <p>
            A next-generation logistics platform built on electric vehicles and smart technology —
            decarbonising supply chains, starting with healthcare in Thailand.
          </p>
          <div className="about-nav-pills">
            <a href="#about">Overview</a>
            <a href="#careers">Careers</a>
            <a href="#news">News</a>
            <a href="#contact">Contact</a>
          </div>
        </div>
      </section>

      {/* ── ABOUT ── */}
      <section className="about-section" id="about">
        <div className="about-section-head">
          <span className="about-eyebrow"><Building2 size={14} /> About Us</span>
          <h2>Smarter Logistics, Cleaner Future</h2>
        </div>
        <div className="about-story">
          <p>
            Axion Mobility is a next-generation logistics platform focused on electric vehicle (EV)
            transportation combined with smart logistics technology. We aim to transform urban and
            regional delivery by providing zero-emission, technology-driven logistics services —
            starting with the healthcare supply chain, where reliability, compliance, and
            traceability are critical.
          </p>
          <p>
            We begin operations with 4-wheel EV trucks designed for medical and healthcare delivery,
            and will expand into EV bikes for last-mile delivery and larger EV trucks for regional
            transport. After establishing strong capabilities in healthcare logistics, Axion will
            grow into food distribution and FMCG supply chains — becoming a comprehensive,
            sustainable logistics network.
          </p>
        </div>
        <div className="about-values">
          {values.map(({ Icon, title, desc }) => (
            <div key={title} className="about-value-card">
              <div className="av-icon"><Icon size={22} strokeWidth={1.7} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        <div className="about-links-row">
          <Link to="/vision" className="about-ghost">Our Vision <ArrowRight size={15} /></Link>
          <Link to="/ecosystem" className="about-ghost">Our Ecosystem <ArrowRight size={15} /></Link>
        </div>
      </section>

      {/* ── CAREERS ── */}
      <section className="about-section about-section--alt" id="careers">
        <div className="about-section-head">
          <span className="about-eyebrow"><Target size={14} /> Careers</span>
          <h2>Join the Movement</h2>
          <p className="about-section-sub">
            We're building the logistics network of the future — and we're looking for people who
            want to make sustainable, technology-driven delivery a reality.
          </p>
        </div>
        <div className="about-values">
          {careerPerks.map(({ Icon, title, desc }) => (
            <div key={title} className="about-value-card">
              <div className="av-icon"><Icon size={22} strokeWidth={1.7} /></div>
              <h3>{title}</h3>
              <p>{desc}</p>
            </div>
          ))}
        </div>
        <div className="about-cta-band">
          <div>
            <h3>Don't see your role? We still want to hear from you.</h3>
            <p>Send us your CV and tell us how you'd like to contribute.</p>
          </div>
          <a href="mailto:dev@akkra.co.th?subject=Careers%20at%20Axion%20Mobility" className="about-btn">
            Send Your CV <ArrowRight size={16} />
          </a>
        </div>
      </section>

      {/* ── NEWS ── */}
      <section className="about-section" id="news">
        <div className="about-section-head">
          <span className="about-eyebrow"><Newspaper size={14} /> News &amp; Updates</span>
          <h2>What's Happening at Axion</h2>
          <p className="about-section-sub">Follow our journey as we roll out our multi-phase expansion strategy.</p>
        </div>
        <div className="about-news">
          {news.map((n) => (
            <div key={n.title} className="about-news-card">
              <div className="anc-top">
                <span className="anc-tag">{n.tag}</span>
                <span className="anc-date">{n.date}</span>
              </div>
              <h3>{n.title}</h3>
              <p>{n.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section className="about-section about-section--alt" id="contact">
        <div className="about-section-head">
          <span className="about-eyebrow"><Phone size={14} /> Contact</span>
          <h2>Get In Touch</h2>
          <p className="about-section-sub">Questions, partnerships, or press — we'd love to hear from you.</p>
        </div>
        <div className="about-contact-grid">
          {contact.map(({ Icon, label, value, href }) => (
            <a key={label} href={href} className="about-contact-card" target={label === 'Visit Us' ? '_blank' : undefined} rel="noreferrer">
              <div className="acc-icon"><Icon size={20} strokeWidth={1.7} /></div>
              <div>
                <p className="acc-label">{label}</p>
                <p className="acc-value">{value}</p>
              </div>
            </a>
          ))}
        </div>
        <div className="about-cta-band">
          <div>
            <h3>Ready to electrify your fleet?</h3>
            <p>Book a demo or request a quote from our team.</p>
          </div>
          <Link to="/contact" className="about-btn">Contact Sales <ArrowRight size={16} /></Link>
        </div>
      </section>
    </div>
  );
}
